import React, { useState, useEffect } from "react";
import { Pressable, ScrollView, View, StyleSheet, ActivityIndicator, TextInput, Platform } from "react-native";
import CCText from "@/Atoms/CCText";
import { useClubDetail } from "@/Hooks/ClubsApi/useClubsQueries";
import { useJoinRequests, useClubInvites } from "@/Hooks/ClubsApi/useClubAdminQueries";
import { useDecideRequest, useMemberAction, useRevokeInvite,
         useSetAnnouncement, useSetRules } from "@/Hooks/ClubsApi/useClubsMutations";
import { useMediaQuery } from "@/Hooks/useMediaQuery/useMediaQuery";

// Avatar helpers (design uses colored circles with initials).
const AVATAR_COLORS = ["#3DAB9E", "#E17846", "#4D3F37", "#67B3E1", "#937ADB"];
const avatarColor = (name: string) =>
  AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];
const initials = (name: string) =>
  (name ?? "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("") || "?";

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FAF6EB",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E8E4DF",
  },
  headerTitle: {
    fontSize: 22,
    color: "#262322",
    flex: 1,
  },
  tabContainer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E8E4DF",
  },
  tab: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#D4CFCA",
  },
  tabActive: {
    backgroundColor: "#3DAB9E",
    borderColor: "#3DAB9E",
  },
  tabText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#4D3F37",
  },
  tabTextActive: {
    color: "#FFFFFF",
  },
  contentContainer: {
    flex: 1,
    padding: 16,
  },
  sectionHeader: {
    fontSize: 12,
    fontWeight: "600",
    color: "#888280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginTop: 20,
    marginBottom: 12,
  },
  requestCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 14,
    padding: 14,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  requestHead: {
    flexDirection: "row",
    alignItems: "center",
    gap: 11,
    marginBottom: 2,
  },
  avatar40: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  avatar38: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginRight: 11,
  },
  avatarInitials: {
    fontSize: 13,
    fontWeight: "600",
    color: "#fff",
  },
  requestName: {
    fontSize: 15,
    fontWeight: "600",
    color: "#262322",
  },
  requestSubtext: {
    fontSize: 11.5,
    color: "#888280",
    marginBottom: 8,
  },
  requestMessage: {
    backgroundColor: "#FAF6EB",
    borderRadius: 10,
    padding: 9,
    marginBottom: 12,
    fontSize: 13,
    color: "#4D3F37",
    fontStyle: "italic",
  },
  buttonRow: {
    flexDirection: "row",
    gap: 8,
  },
  approveBtn: {
    flex: 1,
    backgroundColor: "#3DAB9E",
    borderRadius: 10,
    paddingVertical: 11,
    alignItems: "center",
  },
  approveBtnText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  rejectBtn: {
    flex: 1,
    backgroundColor: "#FFEBEE",
    borderRadius: 10,
    paddingVertical: 11,
    alignItems: "center",
  },
  rejectBtnText: {
    color: "#CD2C39",
    fontSize: 14,
    fontWeight: "600",
  },
  memberRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E8E4DF",
  },
  memberInfo: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  memberName: {
    fontSize: 14.5,
    fontWeight: "600",
    color: "#262322",
  },
  roleTagAdmin: { backgroundColor: "#E4F6EB" },
  roleTagMember: { backgroundColor: "#F1F1F1" },
  roleTextAdmin: { color: "#1E7A3D", fontWeight: "700", letterSpacing: 0.03 },
  roleTextMember: { color: "#888280", fontWeight: "600" },
  roleTag: {
    marginLeft: 8,
    backgroundColor: "#F0EEEB",
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 6,
  },
  roleText: {
    fontSize: 10,
  },
  memberActions: {
    flexDirection: "row",
    gap: 8,
  },
  actionBtn: {
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#4D3F37",
    backgroundColor: "#FFFFFF",
  },
  actionBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#4D3F37",
  },
  removeBtnBorder: {
    borderColor: "#F3D0D5",
  },
  removeBtnText: {
    color: "#CD2C39",
  },
  inviteCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 14,
    padding: 14,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  inviteUrl: {
    fontSize: 13.5,
    fontWeight: "600",
    color: "#262322",
    marginBottom: 8,
  },
  inviteMeta: {
    fontSize: 12,
    color: "#888280",
    marginBottom: 12,
  },
  revokeBtn: {
    backgroundColor: "#FFEBEE",
    borderRadius: 10,
    paddingVertical: 9,
    paddingHorizontal: 14,
    alignItems: "center",
  },
  revokeBtnText: {
    color: "#CD2C39",
    fontSize: 13,
    fontWeight: "600",
  },
  announcementLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#888280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  announcementInput: {
    borderWidth: 1,
    borderColor: "#4D3F37",
    borderRadius: 10,
    paddingHorizontal: 13,
    paddingVertical: 11,
    fontSize: 14,
    color: "#262322",
    minHeight: 90,
    marginBottom: 4,
    textAlignVertical: "top",
    backgroundColor: "#fff",
  },
  charCounter: {
    fontSize: 11,
    color: "#A8A7A6",
    textAlign: "right",
    marginBottom: 12,
  },
  saveBtn: {
    backgroundColor: "#3DAB9E",
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: "center",
  },
  saveBtnText: {
    color: "#FFFFFF",
    fontSize: 13.5,
    fontWeight: "600",
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
  },
  emptyIcon: {
    fontSize: 40,
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 14,
    color: "#888280",
    textAlign: "center",
  },
  wideContainer: {
    flex: 1,
    backgroundColor: "#FAF6EB",
  },
  wideHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 28,
    paddingTop: 22,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E0DDD9",
  },
  wideBackBtn: {
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#E0DDD9",
    borderRadius: 8,
    paddingHorizontal: 13,
    paddingVertical: 8,
  },
  wideBackBtnText: {
    color: "#4D3F37",
    fontWeight: "600",
    fontSize: 13,
  },
  wideTitle: {
    fontSize: 26,
    color: "#262322",
  },
  wideBody: {
    flex: 1,
    flexDirection: "row",
    gap: 22,
    padding: 22,
  },
  wideLeftCol: {
    flex: 1,
    minWidth: 0,
    gap: 22,
  },
  wideRightCol: {
    width: 340,
    gap: 22,
  },
  wideTable: {
    backgroundColor: "#fff",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#E0DDD9",
    overflow: "hidden",
  },
  wideTableHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#E0DDD9",
  },
  wideTableHeaderText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#262322",
  },
  wideRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#E0DDD9",
  },
  wideCard: {
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 18,
    borderWidth: 1,
    borderColor: "#E0DDD9",
  },
});

export default function ClubAdmin({ route, navigation }: any) {
  const { clubId } = route?.params ?? {};
  const goToRoom = () => navigation?.navigate("ClubRoom", { clubId });
  const detail = useClubDetail(clubId);
  const requests = useJoinRequests(clubId);
  const invites = useClubInvites(clubId);
  const decide = useDecideRequest(clubId);
  const memberAction = useMemberAction(clubId);
  const revoke = useRevokeInvite(clubId);
  const setAnnouncement = useSetAnnouncement(clubId);
  const setRules = useSetRules(clubId);
  const [announcement, setAnnouncementText] = useState("");
  const [rules, setRulesText] = useState("");
  const [activeTab, setActiveTab] = useState<"requests" | "members" | "invites" | "settings">("requests");
  const { isMobileOrTab } = useMediaQuery();
  const isWide = !isMobileOrTab;

  const club = detail.data?.club;
  const isClubAdmin = club?.my_role === "admin";

  // Prefill the announcement field with the club's existing pinned message once
  // the detail loads (keyed on the fetched text so it syncs after a save/refetch).
  const existingAnnouncement = club?.announcement?.text ?? "";
  useEffect(() => {
    setAnnouncementText(existingAnnouncement);
  }, [existingAnnouncement]);

  // Prefill the rules editor with the club's configured rules.
  const existingRules = club?.rules ?? "";
  useEffect(() => {
    setRulesText(existingRules);
  }, [existingRules]);

  // Club-rules editor — admin-only text, shown on the club profile.
  const rulesSection = (
    <View>
      <CCText style={styles.announcementLabel}>Club rules</CCText>
      <TextInput
        style={[
          styles.announcementInput,
          { minHeight: 76 },
          Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : null,
        ]}
        testID="rules-input"
        value={rules}
        onChangeText={setRulesText}
        placeholder="Set the ground rules for your club"
        placeholderTextColor="#A8A7A6"
        maxLength={1000}
        multiline
        numberOfLines={3}
      />
      <CCText style={styles.charCounter}>{rules.length}/1000</CCText>
      <Pressable style={styles.saveBtn} testID="rules-save" onPress={() => setRules.mutate(rules)}>
        <CCText style={styles.saveBtnText}>Save rules</CCText>
      </Pressable>
    </View>
  );

  const requestsData = requests.data?.requests ?? [];
  const membersData = club?.members ?? [];
  const invitesData = invites.data?.invites ?? [];

  if (isWide) {
    return (
      <View style={styles.wideContainer}>
        <View style={styles.wideHeader}>
          <Pressable testID="admin-back-room" style={styles.wideBackBtn} onPress={goToRoom}>
            <CCText style={styles.wideBackBtnText}>‹ Room</CCText>
          </Pressable>
          <CCText fontWeight="thunder" style={styles.wideTitle}>Manage · {club?.name ?? ""}</CCText>
        </View>
        <ScrollView contentContainerStyle={styles.wideBody}>
          <View style={styles.wideLeftCol}>
            {/* Join requests table */}
            <View style={styles.wideTable}>
              <View style={styles.wideTableHeader}>
                <CCText style={styles.wideTableHeaderText}>Join requests</CCText>
                {requestsData.length > 0 ? (
                  <CCText style={{ color: "#888280", fontSize: 13 }}>{requestsData.length}</CCText>
                ) : null}
              </View>
              {requestsData.length === 0 ? (
                <View style={{ padding: 30, alignItems: "center" }}>
                  <CCText style={{ color: "#888280", fontSize: 13.5 }}>
                    All caught up — no pending requests.
                  </CCText>
                </View>
              ) : (
                requestsData.map((r: any) => (
                  <View key={r.id} style={styles.wideRow}>
                    <View style={{
                      width: 38, height: 38, borderRadius: 19,
                      backgroundColor: "#3DAB9E", alignItems: "center", justifyContent: "center",
                    }}>
                      <CCText style={{ color: "#fff", fontSize: 13, fontWeight: "600" }}>
                        {r.username.charAt(0).toUpperCase()}
                      </CCText>
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <CCText style={{ fontSize: 14, fontWeight: "600", color: "#262322" }}>{r.name || r.username}</CCText>
                      {r.name ? (
                        <CCText style={{ fontSize: 11.5, color: "#888280" }}>{r.username}</CCText>
                      ) : null}
                      {r.message ? (
                        <CCText style={{ fontSize: 12.5, color: "#888280", fontStyle: "italic" }}>
                          "{r.message}"
                        </CCText>
                      ) : null}
                    </View>
                    <View style={{ flexDirection: "row", gap: 7 }}>
                      <Pressable
                        testID={`req-approve-${r.id}`}
                        onPress={() => decide.mutate({ requestId: r.id, action: "approve" })}
                        style={{ backgroundColor: "#3DAB9E", borderRadius: 8, paddingHorizontal: 16, paddingVertical: 8 }}
                      >
                        <CCText style={{ color: "#fff", fontWeight: "600", fontSize: 13 }}>Approve</CCText>
                      </Pressable>
                      <Pressable
                        testID={`req-reject-${r.id}`}
                        onPress={() => decide.mutate({ requestId: r.id, action: "reject" })}
                        style={{ backgroundColor: "#FFEBEE", borderRadius: 8, paddingHorizontal: 14, paddingVertical: 8 }}
                      >
                        <CCText style={{ color: "#CD2C39", fontWeight: "600", fontSize: 13 }}>Reject</CCText>
                      </Pressable>
                    </View>
                  </View>
                ))
              )}
            </View>

            {/* Members table */}
            <View style={styles.wideTable}>
              <View style={styles.wideTableHeader}>
                <CCText style={styles.wideTableHeaderText}>Members</CCText>
              </View>
              {membersData.map((m: any) => (
                <View key={m.user_id} style={styles.wideRow}>
                  <View style={{
                    width: 34, height: 34, borderRadius: 17,
                    backgroundColor: "#4D3F37", alignItems: "center", justifyContent: "center",
                  }}>
                    <CCText style={{ color: "#fff", fontSize: 12, fontWeight: "600" }}>
                      {m.username.charAt(0).toUpperCase()}
                    </CCText>
                  </View>
                  <View style={{ flex: 1, minWidth: 0, flexDirection: "row", alignItems: "center", gap: 7 }}>
                    <View style={{ flexShrink: 1, minWidth: 0 }}>
                      <CCText numberOfLines={1} style={{ fontSize: 14, fontWeight: "600", color: "#262322" }}>{m.name || m.username}</CCText>
                      {m.name ? (
                        <CCText numberOfLines={1} style={{ fontSize: 11.5, color: "#888280" }}>{m.username}</CCText>
                      ) : null}
                    </View>
                    <View style={styles.roleTag}>
                      <CCText style={styles.roleText}>{m.role}</CCText>
                    </View>
                  </View>
                  {isClubAdmin ? <View style={{ flexDirection: "row", gap: 7 }}>
                    {m.role === "member" ? (
                      <Pressable
                        testID={`member-promote-${m.user_id}`}
                        onPress={() => memberAction.mutate({ userId: m.user_id, action: "promote" })}
                        style={{ backgroundColor: "#fff", borderWidth: 1, borderColor: "#E0DDD9", borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 }}
                      >
                        <CCText style={{ fontSize: 12, fontWeight: "600", color: "#4D3F37" }}>Promote</CCText>
                      </Pressable>
                    ) : (
                      <Pressable
                        testID={`member-demote-${m.user_id}`}
                        onPress={() => memberAction.mutate({ userId: m.user_id, action: "demote" })}
                        style={{ backgroundColor: "#fff", borderWidth: 1, borderColor: "#E0DDD9", borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 }}
                      >
                        <CCText style={{ fontSize: 12, fontWeight: "600", color: "#4D3F37" }}>Demote</CCText>
                      </Pressable>
                    )}
                    <Pressable
                      testID={`member-remove-${m.user_id}`}
                      onPress={() => memberAction.mutate({ userId: m.user_id, action: "remove" })}
                      style={{ backgroundColor: "#fff", borderWidth: 1, borderColor: "#F3D0D5", borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 }}
                    >
                      <CCText style={{ fontSize: 12, fontWeight: "600", color: "#CD2C39" }}>Remove</CCText>
                    </Pressable>
                  </View> : null}
                </View>
              ))}
            </View>
          </View>

          <View style={styles.wideRightCol}>
            {/* Announcement */}
            <View style={styles.wideCard}>
              <CCText style={{ fontSize: 14, fontWeight: "700", color: "#262322", marginBottom: 8 }}>
                Announcement
              </CCText>
              <TextInput
                testID="announcement-input"
                value={announcement}
                onChangeText={setAnnouncementText}
                placeholder="Pin a message for members"
                placeholderTextColor="#A8A7A6"
                maxLength={500}
                multiline
                numberOfLines={3}
                style={[
                  { borderWidth: 1, borderColor: "#4D3F37", borderRadius: 10, paddingHorizontal: 13, paddingVertical: 11, fontSize: 13.5, color: "#262322", minHeight: 76, textAlignVertical: "top", backgroundColor: "#fff" },
                  Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : null,
                ]}
              />
              <CCText style={{ fontSize: 11, color: "#A8A7A6", textAlign: "right", marginTop: 3 }}>
                {announcement.length}/500
              </CCText>
              <Pressable
                testID="announcement-save"
                onPress={() => setAnnouncement.mutate(announcement)}
                style={{ marginTop: 8, backgroundColor: "#3DAB9E", borderRadius: 10, paddingVertical: 9, paddingHorizontal: 16, alignItems: "center" }}
              >
                <CCText style={{ color: "#fff", fontWeight: "600", fontSize: 13 }}>Save & pin</CCText>
              </Pressable>
            </View>

            {/* Club rules */}
            <View style={styles.wideCard}>{rulesSection}</View>

            {/* Active invites */}
            <View style={styles.wideCard}>
              <CCText style={{ fontSize: 14, fontWeight: "700", color: "#262322" }}>Active invites</CCText>
              {invitesData.length === 0 ? (
                <CCText style={{ marginTop: 12, fontSize: 13, color: "#888280" }}>
                  No active links. Create one from the club room.
                </CCText>
              ) : (
                invitesData.map((i: any) => (
                  <View key={i.code} style={{ marginTop: 12, borderWidth: 1, borderColor: "#E0DDD9", borderRadius: 10, padding: 12 }}>
                    <CCText style={{ fontSize: 12.5, fontWeight: "600", color: "#262322" }}>
                      learn.circlechess.com/invite/{i.code}
                    </CCText>
                    <CCText style={{ fontSize: 11.5, color: "#888280", marginTop: 5 }}>
                      {i.used_count}/{i.max_uses} used · expires in 30 days
                    </CCText>
                    {!i.revoked ? (
                      <Pressable
                        testID={`invite-revoke-${i.code}`}
                        onPress={() => revoke.mutate(i.code)}
                        style={{ marginTop: 10, backgroundColor: "#FFEBEE", borderRadius: 8, paddingHorizontal: 13, paddingVertical: 7, alignSelf: "flex-start" }}
                      >
                        <CCText style={{ color: "#CD2C39", fontWeight: "600", fontSize: 12.5 }}>Revoke</CCText>
                      </Pressable>
                    ) : null}
                  </View>
                ))
              )}
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Pressable
          testID="admin-back"
          onPress={goToRoom}
          style={{ paddingVertical: 8, paddingRight: 12 }}>
          <CCText style={{ color: "#4D3F37", fontWeight: "600", fontSize: 15 }}>‹</CCText>
        </Pressable>
        <CCText fontWeight="thunder" style={styles.headerTitle}>
          Manage · {club?.name ?? ""}
        </CCText>
      </View>

      <View style={styles.tabContainer}>
        {(["requests", "members", "invites", "settings"] as const).map((tab) => (
          <Pressable
            key={tab}
            style={[styles.tab, activeTab === tab && styles.tabActive]}
            onPress={() => setActiveTab(tab)}
          >
            <CCText style={[styles.tabText, activeTab === tab && styles.tabTextActive]}>
              {tab === "requests" && `Requests ${requestsData.length > 0 ? requestsData.length : ""}`}
              {tab === "members" && "Members"}
              {tab === "invites" && "Invites"}
              {tab === "settings" && "Settings"}
            </CCText>
          </Pressable>
        ))}
      </View>

      <ScrollView style={styles.contentContainer}>
        {/* Requests Tab */}
        {activeTab === "requests" && (
          <View style={{ gap: 12 }}>
            {requestsData.length === 0 ? (
              <View style={styles.emptyState}>
                <CCText style={styles.emptyIcon}>♟</CCText>
                <CCText style={styles.emptyText}>All caught up — no pending requests.</CCText>
              </View>
            ) : (
              requestsData.map((r: any) => (
                <View key={r.id} style={styles.requestCard}>
                  <View style={styles.requestHead}>
                    <View style={[styles.avatar40, { backgroundColor: avatarColor(r.username) }]}>
                      <CCText style={styles.avatarInitials}>{initials(r.username)}</CCText>
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <CCText style={styles.requestName}>{r.name || r.username}</CCText>
                      {r.name ? (
                        <CCText numberOfLines={1} style={{ fontSize: 11.5, color: "#888280" }}>{r.username}</CCText>
                      ) : null}
                      <CCText style={styles.requestSubtext}>wants to join</CCText>
                    </View>
                  </View>
                  {r.message && (
                    <CCText style={styles.requestMessage}>"{r.message}"</CCText>
                  )}
                  <View style={styles.buttonRow}>
                    <Pressable
                      style={styles.approveBtn}
                      testID={`req-approve-${r.id}`}
                      onPress={() => decide.mutate({ requestId: r.id, action: "approve" })}
                    >
                      <CCText style={styles.approveBtnText}>Approve</CCText>
                    </Pressable>
                    <Pressable
                      style={styles.rejectBtn}
                      testID={`req-reject-${r.id}`}
                      onPress={() => decide.mutate({ requestId: r.id, action: "reject" })}
                    >
                      <CCText style={styles.rejectBtnText}>Reject</CCText>
                    </Pressable>
                  </View>
                </View>
              ))
            )}
          </View>
        )}

        {/* Members Tab */}
        {activeTab === "members" && (
          <View>
            {membersData.length === 0 ? (
              <View style={styles.emptyState}>
                <CCText style={styles.emptyIcon}>♟</CCText>
                <CCText style={styles.emptyText}>No members yet.</CCText>
              </View>
            ) : (
              membersData.map((m: any) => {
                const isAdmin = m.role === "admin";
                return (
                <View key={m.user_id} style={styles.memberRow}>
                  <View style={styles.memberInfo}>
                    <View style={[styles.avatar38, { backgroundColor: avatarColor(m.username) }]}>
                      <CCText style={styles.avatarInitials}>{initials(m.username)}</CCText>
                    </View>
                    <View style={[styles.memberName, { minWidth: 0 }]}>
                      <CCText numberOfLines={1} style={{ fontSize: 14, fontWeight: "600", color: "#262322" }}>{m.name || m.username}</CCText>
                      {m.name ? (
                        <CCText numberOfLines={1} style={{ fontSize: 11.5, color: "#888280" }}>{m.username}</CCText>
                      ) : null}
                    </View>
                    <View style={[styles.roleTag, isAdmin ? styles.roleTagAdmin : styles.roleTagMember]}>
                      <CCText style={[styles.roleText, isAdmin ? styles.roleTextAdmin : styles.roleTextMember]}>
                        {isAdmin ? "Admin" : "Member"}
                      </CCText>
                    </View>
                  </View>
                  {isClubAdmin ? <View style={styles.memberActions}>
                    {m.role === "member" ? (
                      <Pressable
                        style={styles.actionBtn}
                        testID={`member-promote-${m.user_id}`}
                        onPress={() => memberAction.mutate({ userId: m.user_id, action: "promote" })}
                      >
                        <CCText style={styles.actionBtnText}>Promote</CCText>
                      </Pressable>
                    ) : (
                      <Pressable
                        style={styles.actionBtn}
                        testID={`member-demote-${m.user_id}`}
                        onPress={() => memberAction.mutate({ userId: m.user_id, action: "demote" })}
                      >
                        <CCText style={styles.actionBtnText}>Demote</CCText>
                      </Pressable>
                    )}
                    <Pressable
                      style={[styles.actionBtn, styles.removeBtnBorder]}
                      testID={`member-remove-${m.user_id}`}
                      onPress={() => memberAction.mutate({ userId: m.user_id, action: "remove" })}
                    >
                      <CCText style={[styles.actionBtnText, styles.removeBtnText]}>Remove</CCText>
                    </Pressable>
                  </View> : null}
                </View>
                );
              })
            )}
          </View>
        )}

        {/* Invites Tab */}
        {activeTab === "invites" && (
          <View>
            <CCText style={styles.sectionHeader}>Invites</CCText>
            {invitesData.length === 0 ? (
              <View style={styles.emptyState}>
                <CCText style={styles.emptyIcon}>♟</CCText>
                <CCText style={styles.emptyText}>
                  No active invite links. Create one from the club room.
                </CCText>
              </View>
            ) : (
              invitesData.map((i: any) => (
                <View key={i.code} style={styles.inviteCard}>
                  <CCText style={styles.inviteUrl}>
                    learn.circlechess.com/invite/{i.code}
                  </CCText>
                  <CCText style={styles.inviteMeta}>
                    {i.used_count}/{i.max_uses} used · expires in 30 days
                  </CCText>
                  {!i.revoked && (
                    <Pressable
                      style={styles.revokeBtn}
                      testID={`invite-revoke-${i.code}`}
                      onPress={() => revoke.mutate(i.code)}
                    >
                      <CCText style={styles.revokeBtnText}>Revoke link</CCText>
                    </Pressable>
                  )}
                </View>
              ))
            )}
          </View>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <View>
            <CCText style={styles.announcementLabel}>Announcement</CCText>
            <TextInput
              style={[
                styles.announcementInput,
                Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : null,
              ]}
              testID="announcement-input"
              value={announcement}
              onChangeText={setAnnouncementText}
              placeholder="Pin a message for members"
              placeholderTextColor="#A8A7A6"
              maxLength={500}
              multiline
              numberOfLines={3}
            />
            <CCText style={styles.charCounter}>
              {announcement.length}/500
            </CCText>
            <Pressable
              style={styles.saveBtn}
              testID="announcement-save"
              onPress={() => setAnnouncement.mutate(announcement)}
            >
              <CCText style={styles.saveBtnText}>Save & pin</CCText>
            </Pressable>

            <View style={{ height: 22 }} />
            {rulesSection}
          </View>
        )}
      </ScrollView>
    </View>
  );
}
